import React, { Component } from 'react';
import axios from 'axios';
import ReactTable from 'react-table';
import 'react-table/react-table.css';
import {
  Button,
  ButtonToolbar,
  Card,
  CardBody,
  Col,
  Input,
  Label,
  Row,
} from 'reactstrap';

const URL = 'https://api.musicmarquee.co.uk';
const authHeaders = () => ({ Authorization: localStorage.getItem('mm_admin') });

const formatDate = value => (value ? new Date(value).toLocaleString('en-GB') : '');

const formatCsvValue = (value) => {
  if (value === null || value === undefined) {
    return '';
  }

  return `"${`${value}`.replace(/"/g, '""')}"`;
};

class ShipmentHistoryTable extends Component {
  constructor(props) {
    super(props);
    this.state = {
      data: [],
      pages: 0,
      count: 0,
      page: 0,
      pageSize: 50,
      loading: false,
      exporting: false,
      dispatchDateFrom: '',
      dispatchDateTo: '',
      tableState: null,
      tableFilters: [],
      selectedShipment: undefined,
      message: '',
    };
  }

  fetchData = (state) => {
    this.setState({
      loading: true,
      tableState: state,
      page: state.page,
      pageSize: state.pageSize,
    });

    axios.post(
      `${URL}/admin/shipping/shipments/history`,
      {
        page: state.page,
        pageSize: state.pageSize,
        sorted: state.sorted,
        filtered: state.filtered,
        dispatchDateFrom: this.state.dispatchDateFrom,
        dispatchDateTo: this.state.dispatchDateTo,
      },
      { headers: authHeaders() },
    )
      .then((response) => {
        this.setState({
          data: response.data.items || [],
          pages: response.data.pages || 0,
          count: response.data.count || 0,
          loading: false,
          selectedShipment: undefined,
          message: '',
        });
      })
      .catch(() => {
        this.setState({
          loading: false,
          message: 'There was a problem loading shipment history.',
        });
      });
  };

  refreshTable = () => {
    if (this.state.tableState) {
      this.fetchData(Object.assign({}, this.state.tableState, {
        page: this.state.page,
        pageSize: this.state.pageSize,
        filtered: this.state.tableFilters,
      }));
    }
  };

  updateFilter = (field, value) => {
    this.setState({ [field]: value, page: 0 }, () => {
      if (this.state.tableState) {
        this.fetchData(Object.assign({}, this.state.tableState, {
          page: 0,
          pageSize: this.state.pageSize,
          filtered: this.state.tableFilters,
        }));
      }
    });
  };

  updateTableFilters = (tableFilters) => {
    this.setState({ tableFilters, page: 0 });
  };

  resetFilters = () => {
    this.setState({
      dispatchDateFrom: '',
      dispatchDateTo: '',
      tableFilters: [],
      page: 0,
    }, () => {
      if (this.state.tableState) {
        this.fetchData(Object.assign({}, this.state.tableState, {
          page: 0,
          pageSize: this.state.pageSize,
          filtered: [],
        }));
      }
    });
  };

  exportCsv = () => {
    if (!this.state.tableState) {
      return;
    }

    this.setState({ exporting: true });

    axios.post(
      `${URL}/admin/shipping/shipments/history`,
      {
        page: 0,
        pageSize: this.state.count || 100000,
        sorted: this.state.tableState.sorted,
        filtered: this.state.tableFilters,
        dispatchDateFrom: this.state.dispatchDateFrom,
        dispatchDateTo: this.state.dispatchDateTo,
      },
      { headers: authHeaders() },
    )
      .then((response) => {
        const rows = response.data.items || [];
        const columns = [
          { label: 'Customer Ref', key: 'customer_ref' },
          { label: 'Carrier Ref', key: 'carrier_ref' },
          { label: 'Batch ID', key: 'batch_id' },
          { label: 'Carrier', key: 'carrier' },
          { label: 'Dispatch Date', key: 'dispatch_date', format: formatDate },
          { label: 'Customer Name', key: 'customer_name' },
          { label: 'Postcode', key: 'postcode' },
          { label: 'Country', key: 'country' },
          { label: 'Service', key: 'service' },
          { label: 'Service Code', key: 'service_code' },
          { label: 'Package Size', key: 'package_size' },
          { label: 'Tracking', key: 'tracking_no' },
          { label: 'Weight', key: 'weight' },
        ];
        const csvRows = [
          columns.map(column => formatCsvValue(column.label)).join(','),
          ...rows.map(row => columns
            .map((column) => {
              const value = column.format ? column.format(row[column.key]) : row[column.key];
              return formatCsvValue(value);
            })
            .join(',')),
        ];
        const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const date = new Date().toISOString().slice(0, 10);

        link.href = window.URL.createObjectURL(blob);
        link.setAttribute('download', `shipment-history-${date}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        this.setState({ exporting: false });
      })
      .catch(() => {
        this.setState({
          exporting: false,
          message: 'There was a problem exporting shipment history.',
        });
      });
  };

  renderSelectedShipment() {
    if (!this.state.selectedShipment) {
      return null;
    }

    const shipment = this.state.selectedShipment;
    const details = [
      { label: 'Customer Ref', value: shipment.customer_ref },
      { label: 'Carrier Ref', value: shipment.carrier_ref },
      { label: 'Batch ID', value: shipment.batch_id },
      { label: 'Batch Index', value: shipment.batch_index },
      { label: 'Carrier', value: shipment.carrier },
      { label: 'Dispatch Date', value: formatDate(shipment.dispatch_date) },
      { label: 'Customer Name', value: shipment.customer_name },
      { label: 'Postcode', value: shipment.postcode },
      { label: 'Country', value: shipment.country },
      { label: 'Service', value: shipment.service },
      { label: 'Service Code', value: shipment.service_code },
      { label: 'Package Size', value: shipment.package_size },
      { label: 'Tracking', value: shipment.tracking_no },
      { label: 'Weight', value: shipment.weight },
      { label: 'Fulfilment Location', value: shipment.fulfilment_location_id },
    ];

    return (
      <div style={{ marginTop: 16 }}>
        <h5>Shipment Details</h5>
        <table className="table table-sm">
          <tbody>
            {details.map(detail => (
              <tr key={detail.label}>
                <th style={{ width: 180 }}>{detail.label}</th>
                <td>{detail.value || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  render() {
    const columns = [
      { Header: 'Customer Ref', accessor: 'customer_ref' },
      { Header: 'Carrier', accessor: 'carrier', width: 100 },
      {
        Header: 'Dispatch Date',
        accessor: 'dispatch_date',
        Cell: row => formatDate(row.value),
      },
      { Header: 'Customer', accessor: 'customer_name' },
      { Header: 'Postcode', accessor: 'postcode' },
      { Header: 'Country', accessor: 'country', width: 100 },
      { Header: 'Service', accessor: 'service' },
      { Header: 'Service Code', accessor: 'service_code' },
      { Header: 'Package', accessor: 'package_size', width: 100 },
      { Header: 'Tracking', accessor: 'tracking_no' },
      { Header: 'Weight', accessor: 'weight', width: 90 },
    ];

    return (
      <Col md={12}>
        <Card>
          <CardBody>
            <Row style={{ alignItems: 'flex-end' }}>
              <Col md={3}>
                <Label>Dispatch From</Label>
                <Input
                  type="date"
                  value={this.state.dispatchDateFrom}
                  onChange={event => this.updateFilter('dispatchDateFrom', event.target.value)}
                />
              </Col>
              <Col md={3}>
                <Label>Dispatch To</Label>
                <Input
                  type="date"
                  value={this.state.dispatchDateTo}
                  onChange={event => this.updateFilter('dispatchDateTo', event.target.value)}
                />
              </Col>
              <Col md={3}>
                <ButtonToolbar>
                  <Button color="primary" onClick={this.refreshTable}>Refresh</Button>
                  <Button color="secondary" onClick={this.resetFilters}>Reset</Button>
                  <Button color="success" disabled={this.state.exporting} onClick={this.exportCsv}>
                    {this.state.exporting ? 'Exporting...' : 'Export CSV'}
                  </Button>
                </ButtonToolbar>
              </Col>
            </Row>
            {this.state.message ? <p style={{ marginTop: 12 }}>{this.state.message}</p> : null}

            <div style={{ marginTop: 12 }}>
              <ReactTable
                manual
                filterable
                data={this.state.data}
                pages={this.state.pages}
                page={this.state.page}
                pageSize={this.state.pageSize}
                loading={this.state.loading}
                columns={columns}
                filtered={this.state.tableFilters}
                onFilteredChange={this.updateTableFilters}
                onPageChange={page => this.setState({ page })}
                onPageSizeChange={(pageSize, page) => this.setState({ pageSize, page })}
                onFetchData={this.fetchData}
                getTrProps={(state, rowInfo) => {
                  if (rowInfo !== undefined) {
                    return {
                      onClick: () => this.setState({ selectedShipment: rowInfo.original }),
                      style: {
                        background: rowInfo.original === this.state.selectedShipment ? '#00afec' : 'white',
                        color: rowInfo.original === this.state.selectedShipment ? 'white' : 'black',
                        cursor: 'pointer',
                      },
                    };
                  }

                  return {};
                }}
                defaultSorted={[
                  {
                    id: 'dispatch_date',
                    desc: true,
                  },
                ]}
                className="-striped -highlight -responsive"
              />
            </div>
            {this.renderSelectedShipment()}
          </CardBody>
        </Card>
      </Col>
    );
  }
}

export default ShipmentHistoryTable;
